import { FormEvent, KeyboardEvent, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { alpha } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChartContent from '../Analytics/AnalyticsTable/ChartContent';
import EnlargedChartModal from '../Analytics/EnlargedChartModal';
import { AssistantChartPayload } from './chartPayloadAdapter';
import { ChatMessage } from './types';
import { ChippyMessageFeedbackSentiment } from '../../shared/requests/chippyRequests';

interface ChippyChatWindowProps {
  messages: ChatMessage[];
  input: string;
  isTyping: boolean;
  isLoadingMessages: boolean;
  updatingFeedbackMessageId: string | null;
  feedbackFormUrl: string;
  onInputChange: (value: string) => void;
  onSendMessage: () => void;
  onMessageFeedback: (messageId: string, feedback: ChippyMessageFeedbackSentiment) => void;
  onMessageFeedbackComment: (messageId: string, feedbackComment: string | null) => void;
  onDownloadConversation: () => void;
}

const ChippyChatWindow = ({
  messages,
  input,
  isTyping,
  isLoadingMessages,
  updatingFeedbackMessageId,
  feedbackFormUrl,
  onInputChange,
  onSendMessage,
  onMessageFeedback,
  onMessageFeedbackComment,
  onDownloadConversation,
}: ChippyChatWindowProps) => {
  const [expandedChart, setExpandedChart] = useState<AssistantChartPayload | null>(null);
  const [commentTargetMessageId, setCommentTargetMessageId] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const sortedMessages = useMemo(() => [...messages], [messages]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSendMessage();
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSendMessage();
    }
  };

  const handleOpenExpandedChart = (chart: AssistantChartPayload) => {
    setExpandedChart(chart);
  };

  const handleOpenCommentDialog = (message: ChatMessage) => {
    setCommentTargetMessageId(message.id);
    setCommentDraft(message.feedback?.comment ?? '');
  };

  const handleCloseCommentDialog = () => {
    setCommentTargetMessageId(null);
    setCommentDraft('');
  };

  const handleSaveComment = () => {
    if (!commentTargetMessageId) {
      return;
    }
    const trimmedComment = commentDraft.trim();
    onMessageFeedbackComment(commentTargetMessageId, trimmedComment ? trimmedComment : null);
    handleCloseCommentDialog();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        background: (theme) =>
          `linear-gradient(180deg, ${theme.palette.whiteScaleLighter.main} 0%, ${theme.palette.whiteScaleLight.main} 100%)`,
        border: (theme) => `1px solid ${theme.palette.whiteScale.main}`,
        borderRadius: { xs: '16px', md: '22px' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: '1rem', md: '1.25rem' },
          py: { xs: '0.9rem', md: '1.1rem' },
          borderBottom: (theme) => `1px solid ${theme.palette.whiteScale.main}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
          <Avatar
            sx={{
              background: (theme) =>
                `linear-gradient(145deg, ${theme.palette.primaryDarker.main} 0%, ${theme.palette.tertiaryDark.main} 100%)`,
            }}
          >
            <SmartToyOutlinedIcon />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: (theme) => theme.palette.primaryDarker.main,
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              Chippy
            </Typography>
            <Typography
              sx={{
                color: (theme) => alpha(theme.palette.primaryDarker.main, 0.72),
                fontSize: '0.87rem',
              }}
            >
              Internal assistant chat
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={onDownloadConversation}
            sx={{
              textTransform: 'none',
              borderColor: (theme) => theme.palette.whiteScale.main,
              color: (theme) => theme.palette.primaryDarker.main,
              '&:hover': {
                borderColor: (theme) => theme.palette.primary.main,
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            Download Chat
          </Button>
          <Button
            component="a"
            href={feedbackFormUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderColor: (theme) => theme.palette.whiteScale.main,
              color: (theme) => theme.palette.primaryDarker.main,
              '&:hover': {
                borderColor: (theme) => theme.palette.primary.main,
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            Give Feedback
          </Button>
        </Box>
      </Box>

      <EnlargedChartModal
        open={Boolean(expandedChart)}
        onClose={() => setExpandedChart(null)}
        display={
          expandedChart ? (
            <Box sx={{ width: '100%', height: '100%', minHeight: 0 }}>
              <ChartContent
                chartDataParam={{
                  type: expandedChart.type,
                  chartData: expandedChart.chartData,
                  title: expandedChart.title ?? 'Chart',
                  subTitle: expandedChart.subTitle,
                  label: expandedChart.label,
                  isYearlyChart: Boolean(expandedChart.isYearlyChart),
                  analyticsVariant: expandedChart.analyticsVariant ?? 'custom',
                }}
                query={expandedChart.query ?? ''}
                open={true}
                showTitle={true}
              ></ChartContent>
            </Box>
          ) : null
        }
      ></EnlargedChartModal>

      <Box
        role="log"
        aria-live="polite"
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        {isLoadingMessages ? (
          <Typography sx={{ color: (theme) => alpha(theme.palette.primaryDarker.main, 0.7) }}>
            Loading conversation...
          </Typography>
        ) : null}

        {sortedMessages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                maxWidth: { xs: '92%', md: 'min(80%, 620px)' },
                borderRadius: '18px',
                p: '0.75rem 0.9rem',
                bgcolor: (theme) =>
                  message.role === 'user' ? theme.palette.mediaInteraction.main : theme.palette.whiteScaleDark.main,
                color: (theme) =>
                  message.role === 'user'
                    ? theme.palette.mediaInteraction.contrastText
                    : theme.palette.primaryDarker.main,
                ...(message.role === 'user' ? { borderBottomRightRadius: '6px' } : { borderBottomLeftRadius: '6px' }),
              }}
            >
              {message.role !== 'user' ? (
                <Box
                  sx={{
                    '& p': { m: 0, mb: 1.25 },
                    '& p:last-of-type': { mb: 0 },
                    '& h1, & h2, & h3, & h4': { mt: 0, mb: 1, lineHeight: 1.25 },
                    '& ul, & ol': { mt: 0, mb: 1.25, pl: 3 },
                    '& li': { mb: 0.5 },
                    '& code': {
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      backgroundColor: (theme) => alpha(theme.palette.primaryDarker.main, 0.08),
                      px: 0.5,
                      borderRadius: '4px',
                      fontSize: '0.875em',
                    },
                    '& pre': {
                      mt: 0,
                      mb: 1.25,
                      p: 1,
                      borderRadius: '8px',
                      overflowX: 'auto',
                      backgroundColor: (theme) => alpha(theme.palette.primaryDarker.main, 0.08),
                    },
                    '& pre code': {
                      backgroundColor: 'transparent',
                      p: 0,
                    },
                    '& table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                      my: 1.25,
                      fontSize: '0.92rem',
                    },
                    '& th, & td': {
                      border: (theme) => `1px solid ${alpha(theme.palette.primaryDarker.main, 0.25)}`,
                      p: '0.35rem 0.45rem',
                      textAlign: 'left',
                    },
                    '& th': {
                      backgroundColor: (theme) => alpha(theme.palette.primaryDarker.main, 0.08),
                    },
                  }}
                >
                  {message.text ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown> : null}
                  {message.chart?.chartData ? (
                    <Box
                      sx={{
                        position: 'relative',
                        mt: message.text ? 1.25 : 0,
                        width: '100%',
                        minHeight: '280px',
                        height: '320px',
                      }}
                    >
                      <Tooltip title="Expand chart">
                        <IconButton
                          aria-label="Expand chart"
                          onClick={() => {
                            if (message.chart) {
                              handleOpenExpandedChart(message.chart);
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            zIndex: 2,
                            backgroundColor: (theme) => alpha(theme.palette.whiteScaleLighter.main, 0.9),
                            '&:hover': {
                              backgroundColor: (theme) => alpha(theme.palette.whiteScaleLighter.main, 1),
                            },
                          }}
                        >
                          <FullscreenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <ChartContent
                        chartDataParam={{
                          type: message.chart.type,
                          chartData: message.chart.chartData,
                          title: message.chart.title ?? 'Chart',
                          subTitle: message.chart.subTitle,
                          label: message.chart.label,
                          isYearlyChart: Boolean(message.chart.isYearlyChart),
                          analyticsVariant: message.chart.analyticsVariant ?? 'custom',
                        }}
                        query={message.chart.query ?? ''}
                        showCameraButton={false}
                        showTitle={true}
                      ></ChartContent>
                    </Box>
                  ) : null}
                  {message.role === 'assistant' ? (
                    <Box sx={{ mt: 0.6, display: 'flex', alignItems: 'center', gap: 0.2 }}>
                      <Tooltip title="Helpful response">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              onMessageFeedback(message.id, 'up');
                            }}
                            disabled={Boolean(updatingFeedbackMessageId)}
                            sx={{
                              color: (theme) =>
                                message.feedback?.sentiment === 'up'
                                  ? theme.palette.primary.main
                                  : alpha(theme.palette.primaryDarker.main, 0.65),
                            }}
                          >
                            {message.feedback?.sentiment === 'up' ? (
                              <ThumbUpAltIcon fontSize="inherit" />
                            ) : (
                              <ThumbUpAltOutlinedIcon fontSize="inherit" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Not helpful">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              onMessageFeedback(message.id, 'down');
                            }}
                            disabled={Boolean(updatingFeedbackMessageId)}
                            sx={{
                              color: (theme) =>
                                message.feedback?.sentiment === 'down'
                                  ? theme.palette.primary.main
                                  : alpha(theme.palette.primaryDarker.main, 0.65),
                            }}
                          >
                            {message.feedback?.sentiment === 'down' ? (
                              <ThumbDownAltIcon fontSize="inherit" />
                            ) : (
                              <ThumbDownAltOutlinedIcon fontSize="inherit" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          message.feedback?.sentiment
                            ? message.feedback?.comment
                              ? 'Edit feedback comment'
                              : 'Add feedback comment'
                            : 'Select thumbs up or down first'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleOpenCommentDialog(message);
                            }}
                            disabled={Boolean(updatingFeedbackMessageId) || !message.feedback?.sentiment}
                            sx={{
                              color: (theme) =>
                                message.feedback?.comment
                                  ? theme.palette.primary.main
                                  : alpha(theme.palette.primaryDarker.main, 0.65),
                            }}
                          >
                            {message.feedback?.comment ? (
                              <ChatBubbleIcon fontSize="inherit" />
                            ) : (
                              <ChatBubbleOutlineIcon fontSize="inherit" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Typography>
              )}
            </Paper>
          </Box>
        ))}

        {isTyping && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-start',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                maxWidth: { xs: '92%', md: 'min(80%, 620px)' },
                borderRadius: '18px',
                borderBottomLeftRadius: '6px',
                p: '0.75rem 0.9rem',
                bgcolor: (theme) => theme.palette.whiteScaleDark.main,
                color: (theme) => theme.palette.primaryDarker.main,
              }}
            >
              Chippy is typing...
            </Paper>
          </Box>
        )}
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          gap: '0.7rem',
          p: { xs: '0.7rem', md: '0.95rem' },
          borderTop: (theme) => `1px solid ${theme.palette.whiteScale.main}`,
          backgroundColor: (theme) => alpha(theme.palette.whiteScaleLighter.main, 0.9),
        }}
      >
        <TextField
          placeholder="Type a message for Chippy..."
          value={input}
          onChange={(event) => onInputChange(event.target.value)}
          onKeyDown={handleInputKeyDown}
          multiline
          maxRows={4}
          fullWidth
        />
        <Button
          variant="contained"
          type="submit"
          disabled={!input.trim() || isTyping || isLoadingMessages}
          sx={{
            minWidth: '84px',
            color: (theme) => theme.palette.mediaInteraction.contrastText,
            fontWeight: 600,
            background: (theme) =>
              `linear-gradient(145deg, ${theme.palette.primaryDark.main} 0%, ${theme.palette.mediaInteraction.main} 100%)`,
            '&:hover': {
              background: (theme) =>
                `linear-gradient(145deg, ${theme.palette.primaryDarker.main} 0%, ${theme.palette.primaryDark.main} 100%)`,
            },
            '&.Mui-disabled': {
              color: (theme) => alpha(theme.palette.whiteScaleLighter.main, 0.9),
              background: (theme) => theme.palette.whiteScale.main,
            },
          }}
        >
          Send
        </Button>
      </Box>

      <Dialog open={Boolean(commentTargetMessageId)} onClose={handleCloseCommentDialog} fullWidth maxWidth="sm">
        <DialogTitle>Feedback comment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            value={commentDraft}
            onChange={(event) => setCommentDraft(event.target.value)}
            helperText="This helps us understand why this response was helpful or not."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCommentDialog}>Cancel</Button>
          <Button onClick={handleSaveComment} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ChippyChatWindow;
